import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { MdAddCircle, MdDelete } from "react-icons/md";
import { useAuthStore } from "../../store/useAuthStore";
import Loader from "../Loader/Loader";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  borderRadius: "13px",
  boxShadow: 24,
  p: 4,
  overflowY:"scroll",
  height:"80vh",
};

const QueryModal = ({ open, handleClose, onSubmit, loading }) => {
  const { authUser } = useAuthStore()
  const [formData, setFormData] = useState({
    productName: "",
    specifications: [""],
    referenceNumber: "",
    quantity: "",
    expectedDeliveryDate: "",
    budget: "",
    sterile: "",
    email: "",
    fullName: "",
    phoneNumber: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSpecChange = (index, value) => {
    const updatedSpecs = [...formData.specifications];
    updatedSpecs[index] = value;
    setFormData((prev) => ({ ...prev, specifications: updatedSpecs }));
  };

  const handleAddSpec = () => {
    setFormData((prev) => ({
      ...prev,
      specifications: [...prev.specifications, ""],
    }));
  };

  const handleRemoveSpec = (index) => {
    const updatedSpecs = formData.specifications.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, specifications: updatedSpecs }));
  };

  const handleSubmit = async () => {
    const formattedData = {
      ...formData,
      specifications: formData.specifications.filter((s) => s.trim() !== ""),
    };
    let res = await onSubmit(formattedData);
    setFormData({
      productName: "",
      specifications: [""],
      referenceNumber: "",
      quantity: "",
      expectedDeliveryDate: "",
      budget: "",
      sterile: "",
      email: "",
      fullName: "",
      phoneNumber: ""
    })
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "300px",
            }}
          >
            <Typography sx={{ mt: 2, fontWeight: 600, color: "#008BFF" }}>
              Loading...
            </Typography>
          </Box>
        ) : (
          <>
            <Typography
              variant="h6"
              sx={{
                fontFamily: `"Quicksand", sans-serif`,
                fontWeight: 700,
                color: "#008BFF",
                mb: 2,
              }}
            >
              Product Assistance
            </Typography>
            <Typography
              sx={{
                fontWeight: 100,
                color: "#5b5c5eff",
                mb: 2,
              }}
            >
              If you don’t find the product you’re looking for in our catalogue, please reach out to us directly.
              <br />
              <b>Life Line Medical Supply</b>
              can source or manufacture a wide range of specialized and regulated medical items upon request.
              <br />
              To help us provide an accurate and prompt quotation, please include the following details in your inquiry.

            </Typography>

            {/* Fields Grid */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                "& .formControl": { flex: "1 1 calc(50% - 8px)" },
              }}
            >
              <FormControl className="formControl">
                <label>Product Name</label>
                <TextField
                  name="productName"
                  placeholder="Enter Product Name"
                  variant="filled"
                  value={formData.productName}
                  onChange={handleChange}
                  fullWidth
                />
              </FormControl>

              <FormControl className="formControl">
                <label>Reference Number</label>
                <TextField
                  name="referenceNumber"
                  placeholder="Enter Reference Number"
                  variant="filled"
                  value={formData.referenceNumber}
                  onChange={handleChange}
                  fullWidth
                />
              </FormControl>

              <FormControl className="formControl">
                <label>Quantity</label>
                <TextField
                  name="quantity"
                  type="number"
                  placeholder="Enter Quantity"
                  variant="filled"
                  value={formData.quantity}
                  onChange={handleChange}
                  fullWidth
                />
              </FormControl>

              <FormControl className="formControl">
                <label>Expected Delivery Date</label>
                <TextField
                  name="expectedDeliveryDate"
                  type="date"
                  variant="filled"
                  value={formData.expectedDeliveryDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </FormControl>

              <FormControl className="formControl">
                <label>
                  Budget ($) <span style={{ fontWeight: 400, color: "#6b7280" }}>per piece</span>
                </label>

                <TextField
                  name="budget"
                  type="number"
                  placeholder="Enter Budget"
                  variant="filled"
                  value={formData.budget}
                  onChange={handleChange}
                  fullWidth
                />
              </FormControl>

              {/* Sterility Requirement Dropdown */}
              <FormControl className="formControl" variant="filled" fullWidth>
                <InputLabel
                  id="sterile-label"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    color: "#000",
                    mb: 0.5,
                  }}
                >
                  Sterility Requirement
                </InputLabel>
                <Select
                  labelId="sterile-label"
                  name="sterile"
                  value={formData.sterile}
                  onChange={handleChange}
                  displayEmpty
                  sx={{ borderRadius: "4px" }}
                >
                  {/* <MenuItem value="">
                <em>Select Option</em>
              </MenuItem> */}
                  <MenuItem value="Sterile">Sterile</MenuItem>
                  <MenuItem value="Non-Sterile">Non-Sterile</MenuItem>
                </Select>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#555",
                    fontSize: "13px",
                    mt: 0.5,
                    fontStyle: "italic",
                  }}
                >
                  Indicate whether you need sterile or non-sterile components for
                  your PACs (Procedural Access Kits) or other assemblies.
                </Typography>
              </FormControl>

              {
                authUser == null &&
                <>
                  <FormControl className="formControl">
                    <label>Email</label>
                    <TextField
                      name="email"
                      type="email"
                      placeholder="Enter Your Email Address"
                      variant="filled"
                      value={formData.email}
                      onChange={handleChange}
                      fullWidth
                    />
                  </FormControl>

                  <FormControl className="formControl">
                    <label>Phone Number</label>
                    <TextField
                      name="phoneNumber"
                      type="String"
                      placeholder="Enter Your Phone Number"
                      variant="filled"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      fullWidth
                    />
                  </FormControl>

                  <FormControl className="formControl">
                    <label>Full Name</label>
                    <TextField
                      name="fullName"
                      type="String"
                      placeholder="Enter Your Full Name"
                      variant="filled"
                      value={formData.fullName}
                      onChange={handleChange}
                      fullWidth
                    />
                  </FormControl>
                </>
              }
            </Box>

            {/* Specifications (Full Width) */}
            <FormControl fullWidth sx={{ 
              mt: 3,
             }}>
              <label>Specifications</label>
              {formData.specifications.map((spec, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <TextField
                    variant="filled"
                    placeholder={`Specification ${index + 1}`}
                    value={spec}
                    onChange={(e) => handleSpecChange(index, e.target.value)}
                    fullWidth
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveSpec(index)}
                    disabled={formData.specifications.length === 1}
                  >
                    <MdDelete />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<MdAddCircle />}
                onClick={handleAddSpec}
                sx={{
                  textTransform: "capitalize",
                  color: "#008BFF",
                  fontWeight: 500,
                  mt: 1,
                }}
              >
                Add Specification
              </Button>
            </FormControl>

            {/* Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
                gap: 2,
              }}
            >
              <Button
                onClick={handleClose}
                sx={{ color: "#000", textTransform: "capitalize" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                sx={{
                  background: "#008BFF",
                  textTransform: "capitalize",
                  fontWeight: 500,
                  borderRadius: "4px",
                }}
              >
                Submit Query
              </Button>
            </Box>
          </>)}
      </Box>
    </Modal >
  );
};

export default QueryModal;
